import { ROOM_TYPE_IDS } from 'features/Home/constants/room.constants'
import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'

export const UNASSIGNED_SCHEDULE_STAFF_ID = 0

export type ScheduleInstanceView = 'today' | 'day' | 'week'

export const ALL_STAFF_INSTANCE_ID = 'all'

export const buildScheduleInstanceKey = ({
  view,
  date,
  staffId,
}: {
  view: ScheduleInstanceView
  date: string
  staffId: string | number
}): string => `${view}:${date}:staff:${staffId}`

export const getScheduleStaffId = (event: Pick<IPatientSchedule, 'consultation_staff'>): number =>
  Number(event.consultation_staff) || UNASSIGNED_SCHEDULE_STAFF_ID

export const isAllConsultationStaffSelected = (
  selectedConsultationStaffIds: string[] | null | undefined,
): boolean => selectedConsultationStaffIds == null || selectedConsultationStaffIds.includes('-1')

export const isNoConsultationStaffSelected = (
  selectedConsultationStaffIds: string[] | null | undefined,
): boolean =>
  Array.isArray(selectedConsultationStaffIds) && selectedConsultationStaffIds.length === 0

export const filterSchedulesBySelectedStaff = (
  items: IPatientSchedule[],
  selectedConsultationStaffIds: string[] | null | undefined,
): IPatientSchedule[] => {
  if (isAllConsultationStaffSelected(selectedConsultationStaffIds)) return items

  const selectedIds = new Set(selectedConsultationStaffIds.map(String))
  return items.filter((item) => selectedIds.has(String(item.consultation_staff || 0)))
}

export type ScheduleStaffRole = {
  id: number
  name?: string
  is_doctor?: number
  is_provider?: number
  is_medical_staff?: number
}

export const isScheduleRailStaff = (
  staff: Pick<ScheduleStaffRole, 'is_doctor' | 'is_provider'>,
): boolean => Boolean(staff.is_doctor || staff.is_provider)

export const getScheduleDoctorRailStaff = <
  T extends Pick<ScheduleStaffRole, 'is_doctor' | 'is_provider'>,
>(
  staff: T[],
): T[] => staff.filter(isScheduleRailStaff)

export const orderSavedIds = <T extends string | number>(defaultIds: T[], savedIds: T[]): T[] => {
  const present = new Set(defaultIds)
  const saved = savedIds.filter((id, index) => present.has(id) && savedIds.indexOf(id) === index)
  if (!saved.length) return defaultIds

  const placed = new Set(saved)
  const result = [...saved]

  for (const id of defaultIds) {
    if (placed.has(id)) continue

    const index = defaultIds.indexOf(id)
    let anchorAfter: T | undefined
    for (let i = index - 1; i >= 0; i--) {
      if (placed.has(defaultIds[i])) {
        anchorAfter = defaultIds[i]
        break
      }
    }

    if (anchorAfter != null) {
      result.splice(result.indexOf(anchorAfter) + 1, 0, id)
    } else {
      let anchorBefore: T | undefined
      for (let i = index + 1; i < defaultIds.length; i++) {
        if (placed.has(defaultIds[i])) {
          anchorBefore = defaultIds[i]
          break
        }
      }
      if (anchorBefore != null) result.splice(result.indexOf(anchorBefore), 0, id)
      else result.push(id)
    }

    placed.add(id)
  }

  return result
}

export const orderScheduleDoctorRailIds = (defaultIds: number[], savedIds: number[]): number[] =>
  orderSavedIds(defaultIds, savedIds)

export const applyScheduleDoctorRailOrder = <T extends { id: number }>(
  staff: T[],
  savedIds: number[],
): T[] => {
  if (!savedIds.length || staff.length < 2) return staff
  const byId = new Map(staff.map((member) => [member.id, member]))
  return orderScheduleDoctorRailIds(
    staff.map((member) => member.id),
    savedIds,
  ).flatMap((id) => {
    const member = byId.get(id)
    return member ? [member] : []
  })
}

export const mergeReorderedIds = <T extends string | number>(
  previousIds: T[],
  nextVisibleIds: T[],
): T[] => {
  const visible = new Set(nextVisibleIds)
  const result: T[] = []
  let inserted = false

  for (const id of previousIds) {
    if (visible.has(id)) {
      if (!inserted) {
        result.push(...nextVisibleIds)
        inserted = true
      }
      continue
    }
    if (!result.includes(id)) result.push(id)
  }

  if (!inserted) {
    for (const id of nextVisibleIds) {
      if (!result.includes(id)) result.push(id)
    }
  }

  return result
}

export const mergeReorderedDoctorRailIds = (
  previousIds: number[],
  nextVisibleIds: number[],
): number[] => mergeReorderedIds(previousIds, nextVisibleIds)

export const getScheduleStaffRoleRank = (
  staff: Pick<ScheduleStaffRole, 'id' | 'is_doctor' | 'is_provider'>,
): number => {
  if (staff.id === UNASSIGNED_SCHEDULE_STAFF_ID) return 3
  if (staff.is_doctor) return 0
  if (staff.is_provider) return 1
  return 2
}

export const compareScheduleStaffByRole = (
  left: Pick<ScheduleStaffRole, 'id' | 'name' | 'is_doctor' | 'is_provider'>,
  right: Pick<ScheduleStaffRole, 'id' | 'name' | 'is_doctor' | 'is_provider'>,
): number => {
  const rankDiff = getScheduleStaffRoleRank(left) - getScheduleStaffRoleRank(right)
  if (rankDiff !== 0) return rankDiff
  return (left.name ?? '').localeCompare(right.name ?? '')
}

const withStaffRoles = (
  staff: IPatientConsultationStaff,
  roleById: Map<number, ScheduleStaffRole>,
): IPatientConsultationStaff => {
  const role = roleById.get(staff.id)
  if (!role) return staff
  return {
    ...staff,
    is_doctor: role.is_doctor ?? staff.is_doctor,
    is_provider: role.is_provider ?? staff.is_provider,
    is_medical_staff: role.is_medical_staff ?? staff.is_medical_staff,
  }
}

export const getVisibleScheduleStaff = (
  consultationStaffs: IPatientConsultationStaff[],
  schedules: IPatientSchedule[],
  selectedConsultationStaffIds: string[] | string | null | undefined,
  roleUsers: ScheduleStaffRole[] = [],
): IPatientConsultationStaff[] => {
  const selectedIds = Array.isArray(selectedConsultationStaffIds)
    ? selectedConsultationStaffIds
    : selectedConsultationStaffIds
    ? [selectedConsultationStaffIds]
    : null

  const roleById = new Map(roleUsers.map((user) => [user.id, user]))
  const staffById = new Map<number, IPatientConsultationStaff>()

  for (const staff of consultationStaffs) {
    staffById.set(staff.id, withStaffRoles(staff, roleById))
  }

  for (const event of schedules) {
    const staffId = getScheduleStaffId(event)
    if (staffById.has(staffId)) continue

    const stub: IPatientConsultationStaff = {
      id: staffId,
      name:
        staffId === UNASSIGNED_SCHEDULE_STAFF_ID
          ? 'Unassigned'
          : event.consultation_staff_name || `Staff ${staffId}`,
      is_doctor: 0,
      procedure: event.procedure,
    }
    staffById.set(staffId, withStaffRoles(stub, roleById))
  }

  const allStaff = [...staffById.values()].sort(compareScheduleStaffByRole)

  if (isAllConsultationStaffSelected(selectedIds)) {
    return allStaff
  }

  const selectedSet = new Set(selectedIds.map(String))
  const selected = allStaff.filter((staff) => selectedSet.has(String(staff.id)))

  for (const id of selectedIds) {
    const selectedId = Number(id)
    if (!Number.isFinite(selectedId) || id === '-1') continue
    if (selected.some((staff) => staff.id === selectedId)) continue
    const stub: IPatientConsultationStaff = {
      id: selectedId,
      name: `Staff ${selectedId}`,
      is_doctor: 0,
      procedure: consultationStaffs[0]?.procedure ?? { code: null, description: null },
    }
    selected.push(withStaffRoles(stub, roleById))
  }

  return selected.sort(compareScheduleStaffByRole)
}

const SCHEDULE_STAFF_COLORS = [
  '#1f6f4a',
  '#b2561d',
  '#2f5aa8',
  '#7a2f7a',
  '#c0392b',
  '#1a7a7a',
  '#8a6d1d',
]

export const getScheduleStaffColorByIndex = (index: number): string =>
  SCHEDULE_STAFF_COLORS[Math.max(0, index) % SCHEDULE_STAFF_COLORS.length]

export const getScheduleStaffColor = (staffId: number): string => {
  if (staffId === UNASSIGNED_SCHEDULE_STAFF_ID) return '#6f6a62'
  return SCHEDULE_STAFF_COLORS[Math.abs(staffId) % SCHEDULE_STAFF_COLORS.length]
}

export const getScheduleStaffShortName = (name: string): string => {
  const trimmed = name.replace(/^Dr\.?\s*/i, '').trim()
  if (!trimmed) return name
  if (trimmed.includes(',')) return trimmed.split(',')[0]?.trim() || trimmed
  const parts = trimmed.split(/\s+/).filter(Boolean)
  return parts[parts.length - 1] || trimmed
}

export const getScheduleStaffInitials = (name: string): string => {
  const trimmed = name.replace(/^Dr\.?\s*/i, '').trim()
  if (!trimmed) return ''

  if (trimmed.includes(',')) {
    const [lastName, firstNames] = trimmed.split(',').map((part) => part.trim())
    const firstName = firstNames?.split(/\s+/).filter(Boolean)[0] || lastName || ''
    const last = lastName || firstName
    if (firstName && last) return `${firstName.charAt(0)}${last.charAt(0)}`.toUpperCase()
    return trimmed.slice(0, 2).toUpperCase()
  }

  const parts = trimmed.split(/\s+/).filter(Boolean)
  const firstName = parts[0] || ''
  const lastName = parts[parts.length - 1] || firstName
  if (firstName && lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  return trimmed.slice(0, 2).toUpperCase()
}

export const getWaitingRoomStaffFilterTag = (
  focusedStaff: Pick<IPatientConsultationStaff, 'name'>[],
): string => {
  if (!focusedStaff.length) return ''
  const initials = focusedStaff.map((staff) => getScheduleStaffInitials(staff.name)).filter(Boolean)
  if (!initials.length) return ''
  return `${initials.join(' · ')} only`
}

export const isWaitingRoomSchedule = (
  event: Pick<IPatientSchedule, 'room' | 'room_id'> & { room_type_id?: number },
): boolean =>
  Boolean(event.room?.is_waiting_room) ||
  event.room?.room_type_id === ROOM_TYPE_IDS.WAITING_ROOM ||
  event.room_type_id === ROOM_TYPE_IDS.WAITING_ROOM

type WaitingRoomCatalogEntry = {
  label?: string
  value?: string | number
  code?: string | number
  is_waiting_room?: boolean | number
  room_type_id?: number
}

const isWaitingRoomCatalogEntry = (room: WaitingRoomCatalogEntry): boolean =>
  Boolean(room.is_waiting_room) || room.room_type_id === ROOM_TYPE_IDS.WAITING_ROOM

const scheduleResourceId = (event: Pick<IPatientSchedule, 'room' | 'room_id'>): string => {
  if (Array.isArray(event.room_id)) return String(event.room_id[0] ?? '')
  if (event.room_id != null && event.room_id !== '') return String(event.room_id)
  if (event.room?.label != null && event.room.value != null) {
    return `${event.room.label}/${event.room.value}`
  }
  return ''
}

export const filterWaitingRoomSchedulesForStaffFocus = (
  schedules: IPatientSchedule[],
  focusedStaffIds: number[],
  rooms?: WaitingRoomCatalogEntry[],
): IPatientSchedule[] => {
  if (!focusedStaffIds.length) return schedules
  const selected = new Set(focusedStaffIds)
  const waitingValues = new Set<string>()
  const waitingResourceIds = new Set<string>()
  for (const room of rooms ?? []) {
    if (!isWaitingRoomCatalogEntry(room)) continue
    if (room.value != null) waitingValues.add(String(room.value))
    if (room.code != null) waitingValues.add(String(room.code))
    if (room.label != null && room.value != null) {
      waitingResourceIds.add(`${room.label}/${room.value}`)
    }
  }

  return schedules.filter((event) => {
    const roomValue = event.room?.value ?? event.room?.code
    const inWaitingRoom =
      isWaitingRoomSchedule(event) ||
      (roomValue != null && waitingValues.has(String(roomValue))) ||
      waitingResourceIds.has(scheduleResourceId(event))
    if (!inWaitingRoom) return true
    return selected.has(getScheduleStaffId(event))
  })
}

export const filterSchedulesForStaffFocus = (
  schedules: IPatientSchedule[],
  focusedStaffIds: number[],
): IPatientSchedule[] => {
  if (!focusedStaffIds.length) return schedules
  const selected = new Set(focusedStaffIds)
  return schedules.filter((event) => selected.has(getScheduleStaffId(event)))
}
